import React, { useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { datosDeVenta, comprobanteDeVenta, emitirComprobante, guardarRtnCliente, numeroComprobante } from '../services/comprobantesService';
import { obtenerCaiActivo, estadoCai, facturaDeVenta, emitirFactura, anularFactura } from '../services/facturasService';
import { armarComprobantePdf, descargarPdf, compartirPdf, imprimirPdf } from '../utils/reciboPdf';
import { armarFacturaPdf } from '../utils/facturaPdf';
import { mensajeAmigable } from '../utils/errores';

// Documento de una venta (ticket):
// - Comprobante de venta (no fiscal): carta o ticket 80 mm.
// - Factura con CAI (SAR): solo carta, original + copia; se emite una vez y
//   queda congelada; si tiene un error se anula (no se borra).
const ERRORES_FACTURA = {
  SIN_CAI: 'No hay un CAI registrado. La dueña lo registra en ⚙️ Configuración.',
  CAI_VENCIDO: 'El CAI ya venció (pasó la fecha límite de emisión). Registra el CAI nuevo en ⚙️ Configuración.',
  RANGO_AGOTADO: 'Se acabó el rango de facturas autorizado. Registra el CAI nuevo en ⚙️ Configuración.',
  DATOS_EMISOR_INCOMPLETOS: 'Faltan el RTN o la dirección del negocio en ⚙️ Configuración.',
  RTN_INVALIDO: 'El RTN debe tener 14 números (o la identidad 13 números).',
  NOMBRE_REQUERIDO: 'Escribe el nombre del cliente junto con su RTN.',
  DATOS_CLIENTE_REQUERIDOS: 'Las ventas de más de L 10,000 a consumidor final llevan nombre y número de identidad del cliente.',
  SOLO_LA_DUENA: 'Solo la dueña puede anular facturas.',
  MOTIVO_REQUERIDO: 'Escribe el motivo de la anulación.',
};
const errorFactura = (e) => {
  const m = String(e?.message || '');
  const clave = Object.keys(ERRORES_FACTURA).find((k) => m.includes(k));
  return clave ? ERRORES_FACTURA[clave] : mensajeAmigable(e);
};
const L = (n) => `L ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ComprobanteModal({ ticket, onCerrar }) {
  const { mostrarToast } = useToast();
  const { esVendedora } = useAuth();
  const [tipo, setTipo] = useState('comprobante');
  const [datos, setDatos] = useState(null);
  const [comprobante, setComprobante] = useState(null);
  const [cai, setCai] = useState(null);
  const [factura, setFactura] = useState(null);
  const [nombre, setNombre] = useState('');
  const [rtn, setRtn] = useState('');
  const [exonerado, setExonerado] = useState(false);
  const [exo, setExo] = useState({ ordenCompra: '', constancia: '', registroSag: '' });
  const [formato, setFormato] = useState('carta');
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let activo = true;
    Promise.all([datosDeVenta(ticket), comprobanteDeVenta(ticket), obtenerCaiActivo().catch(() => null), facturaDeVenta(ticket).catch(() => null)])
      .then(([d, c, ca, f]) => {
        if (!activo) return;
        setDatos(d); setComprobante(c); setCai(ca); setFactura(f);
        if (f || ca) setTipo('factura');
        setNombre(c?.cliente_nombre || d.venta.cliente?.nombre || '');
        setRtn(c?.cliente_rtn || d.venta.cliente?.rtn || '');
      })
      .catch((e) => activo && setError(mensajeAmigable(e)));
    return () => { activo = false; };
  }, [ticket]);

  const guardarRtn = () => {
    const clienteId = datos.venta.clienteId;
    if (clienteId && rtn.trim() && rtn.trim() !== (datos.venta.cliente?.rtn || '')) guardarRtnCliente(clienteId, rtn).catch(() => {});
  };

  const accion = async (pdf, que, texto) => {
    if (que === 'compartir') await compartirPdf(pdf, texto);
    else if (que === 'imprimir') imprimirPdf(pdf);
    else descargarPdf(pdf);
  };

  const hacerComprobante = async (que) => {
    setTrabajando(true);
    try {
      const c = await emitirComprobante(ticket, { clienteNombre: nombre, clienteRtn: rtn });
      setComprobante(c); guardarRtn();
      const pdf = await armarComprobantePdf({ negocio: datos.negocio, venta: datos.venta, comprobante: c,
        cliente: { nombre: nombre.trim(), rtn: rtn.trim() }, formato });
      await accion(pdf, que, `Comprobante ${numeroComprobante(c.numero)} · ${datos.negocio.nombre || ''}`);
    } catch (e) { mostrarToast(mensajeAmigable(e), 'error'); } finally { setTrabajando(false); }
  };

  const emitir = async () => {
    if (!window.confirm('Una vez emitida, la factura no se puede modificar (solo anular). ¿Revisaste el nombre y el RTN del cliente?')) return;
    setTrabajando(true);
    try {
      const f = await emitirFactura(ticket, { nombre, rtn, ...(exonerado ? exo : {}) });
      setFactura(f); guardarRtn();
      setCai(await obtenerCaiActivo().catch(() => cai));
      mostrarToast(`✅ Factura ${f.numero} emitida`, 'exito');
    } catch (e) { mostrarToast(errorFactura(e), 'error', 7000); } finally { setTrabajando(false); }
  };

  const usarFactura = async (que) => {
    setTrabajando(true);
    try { await accion(await armarFacturaPdf(factura), que, `Factura ${factura.numero} · ${factura.emisor?.nombre || ''}`); }
    catch (e) { mostrarToast(mensajeAmigable(e), 'error'); } finally { setTrabajando(false); }
  };

  const anular = async () => {
    const motivo = window.prompt(`Anular la factura ${factura.numero}.\nNo se borra: queda guardada con la leyenda ANULADA.\n\nEscribe el motivo:`);
    if (motivo === null) return;
    setTrabajando(true);
    try {
      const f = await anularFactura(factura.id, motivo);
      mostrarToast(`Factura ${f.numero} anulada. Ya puedes emitir una nueva para esta venta.`, 'exito', 6000);
      setFactura(null);
    } catch (e) { mostrarToast(errorFactura(e), 'error', 6000); } finally { setTrabajando(false); }
  };

  const campo = { width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '9px', fontSize: '15px', fontFamily: 'inherit', boxSizing: 'border-box' };
  const etq = { display: 'block', fontWeight: 600, fontSize: '13px', marginBottom: '4px' };
  const pestaña = (valor, texto, activo, onClick) => (
    <button type="button" onClick={onClick} disabled={trabajando}
      style={{ flex: 1, padding: '10px', borderRadius: '9px', cursor: 'pointer', fontWeight: 700, fontSize: '13px', fontFamily: 'inherit',
        border: activo ? '2px solid var(--color-primario,#6D28D9)' : '1px solid #D1D5DB',
        background: activo ? '#F5F3FF' : '#fff', color: '#1C1830' }}>{texto}</button>
  );
  const botonesPdf = (fn) => (
    <div style={{ display: 'grid', gap: '8px' }}>
      <button onClick={() => fn('compartir')} disabled={trabajando}
        style={{ padding: '12px', background: 'var(--color-primario,#6D28D9)', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '15px', cursor: 'pointer' }}>
        {trabajando ? '⏳ Preparando…' : '📲 Compartir (WhatsApp, correo…)'}
      </button>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={() => fn('descargar')} disabled={trabajando} className="k-btn-soft" style={{ flex: 1 }}>⬇️ Descargar</button>
        <button onClick={() => fn('imprimir')} disabled={trabajando} className="k-btn-soft" style={{ flex: 1 }}>🖨️ Imprimir</button>
      </div>
    </div>
  );
  const camposCliente = (bloqueado) => (
    <>
      <label style={etq}>Nombre del cliente</label>
      <input value={nombre} onChange={(e) => setNombre(e.target.value)} maxLength={120} disabled={trabajando || bloqueado}
        placeholder="Consumidor final" style={{ ...campo, marginBottom: '10px' }} />
      <label style={etq}>{tipo === 'factura' ? 'RTN del cliente (o identidad)' : 'RTN del cliente (si lo pide)'}</label>
      <input value={rtn} onChange={(e) => setRtn(e.target.value)} maxLength={20} inputMode="numeric" disabled={trabajando || bloqueado}
        placeholder={tipo === 'factura' ? 'Vacío = Consumidor final' : 'Opcional'} style={{ ...campo, marginBottom: '12px' }} />
    </>
  );

  const est = estadoCai(cai);

  return (
    <div role="dialog" aria-modal="true" onClick={onCerrar}
      style={{ position: 'fixed', inset: 0, background: 'rgba(17,12,34,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1500, padding: '16px' }}>
      <div onClick={(e) => e.stopPropagation()} className="k-card"
        style={{ background: '#fff', width: 'min(460px, 100%)', maxHeight: '92vh', overflowY: 'auto', borderRadius: '16px', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h3 style={{ margin: 0 }}>{tipo === 'factura' ? '🏛️ Factura' : '🧾 Comprobante de venta'}</h3>
          <button onClick={onCerrar} aria-label="Cerrar" style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#6B7280' }}>×</button>
        </div>

        {error && <p style={{ color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</p>}
        {!datos && !error && <p style={{ color: '#6B7280' }}>⏳ Cargando la venta…</p>}

        {datos && (
          <>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
              {pestaña('comprobante', '🧾 Comprobante', tipo === 'comprobante', () => setTipo('comprobante'))}
              {pestaña('factura', '🏛️ Factura (CAI)', tipo === 'factura', () => setTipo('factura'))}
            </div>
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6B7280' }}>Venta de {L(datos.venta.total)} · {datos.venta.items.length} producto(s)</p>

            {tipo === 'comprobante' && (
              <>
                {comprobante && <p style={{ margin: '0 0 10px', fontSize: '13px' }}>Número <b>{numeroComprobante(comprobante.numero)}</b></p>}
                {camposCliente(false)}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                  {pestaña('carta', '📄 Carta', formato === 'carta', () => setFormato('carta'))}
                  {pestaña('ticket', '🧾 Ticket 80 mm', formato === 'ticket', () => setFormato('ticket'))}
                </div>
                {botonesPdf(hacerComprobante)}
                <p style={{ margin: '12px 0 0', fontSize: '11.5px', color: '#9CA3AF' }}>Comprobante interno de venta, no sustituye la factura fiscal.</p>
              </>
            )}

            {tipo === 'factura' && factura && (
              <>
                <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px', fontSize: '13.5px', lineHeight: 1.6 }}>
                  <div>✅ Factura <b>{factura.numero}</b></div>
                  <div>Cliente: {factura.cliente_nombre}{factura.cliente_rtn ? ` · RTN ${factura.cliente_rtn}` : ''}</div>
                  <div>Total: <b>{L(factura.total)}</b> · ISV: {L(Number(factura.isv_15) + Number(factura.isv_18))}</div>
                </div>
                {botonesPdf(usarFactura)}
                <p style={{ margin: '10px 0 0', fontSize: '11.5px', color: '#6B7280' }}>Sale en carta con 2 páginas: Original (cliente) y Copia (para ti). No se imprime en papel térmico (art. 38).</p>
                {!esVendedora && (
                  <button onClick={anular} disabled={trabajando} className="k-btn-soft" style={{ marginTop: '12px', color: '#B91C1C', width: '100%' }}>⛔ Anular esta factura</button>
                )}
              </>
            )}

            {tipo === 'factura' && !factura && !est.puede && (
              <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '12px', fontSize: '13.5px', lineHeight: 1.6 }}>
                {est.motivo === 'sin_cai' && <>Para emitir facturas necesitas un <b>CAI</b> del SAR. La dueña lo registra en <b>⚙️ Configuración → Factura con CAI</b>.</>}
                {est.motivo === 'vencido' && <>⛔ El CAI venció el {cai.fecha_limite}. Registra el CAI nuevo en ⚙️ Configuración.</>}
                {est.motivo === 'agotado' && <>⛔ Se acabó el rango de facturas autorizado. Registra el CAI nuevo en ⚙️ Configuración.</>}
                <div style={{ marginTop: '6px' }}>Mientras tanto puedes dar un <b>Comprobante de venta</b>.</div>
              </div>
            )}

            {tipo === 'factura' && !factura && est.puede && (
              <>
                {est.aviso === 'por_vencer' && <p style={{ margin: '0 0 10px', fontSize: '12.5px', color: '#B45309', fontWeight: 600 }}>⚠️ Tu CAI vence en {est.dias} días.</p>}
                {est.aviso === 'por_agotarse' && <p style={{ margin: '0 0 10px', fontSize: '12.5px', color: '#B45309', fontWeight: 600 }}>⚠️ Te quedan {est.quedan} facturas en el rango.</p>}
                {camposCliente(false)}
                {datos.venta.total > 10000 && (
                  <p style={{ margin: '-4px 0 10px', fontSize: '12.5px', color: '#B45309' }}>Más de L 10,000: lleva nombre y RTN o número de identidad del cliente.</p>
                )}
                <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13.5px', marginBottom: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={exonerado} onChange={(e) => setExonerado(e.target.checked)} disabled={trabajando} />
                  Cliente exonerado de ISV
                </label>
                {exonerado && (
                  <div style={{ background: '#F9FAFB', borderRadius: '10px', padding: '10px', marginBottom: '12px' }}>
                    <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#6B7280' }}>Llena el que corresponda. La venta sale como "Importe exonerado", sin ISV.</p>
                    <label style={etq}>No. Orden de Compra Exenta</label>
                    <input value={exo.ordenCompra} onChange={(e) => setExo({ ...exo, ordenCompra: e.target.value })} style={{ ...campo, marginBottom: '8px' }} />
                    <label style={etq}>No. Constancia de Registro de Exonerados</label>
                    <input value={exo.constancia} onChange={(e) => setExo({ ...exo, constancia: e.target.value })} style={{ ...campo, marginBottom: '8px' }} />
                    <label style={etq}>No. Registro SAG</label>
                    <input value={exo.registroSag} onChange={(e) => setExo({ ...exo, registroSag: e.target.value })} style={campo} />
                  </div>
                )}
                <button onClick={emitir} disabled={trabajando}
                  style={{ width: '100%', padding: '12px', background: '#059669', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '15px', cursor: 'pointer' }}>
                  {trabajando ? '⏳ Emitiendo…' : '🏛️ Emitir factura'}
                </button>
                <p style={{ margin: '10px 0 0', fontSize: '11.5px', color: '#6B7280' }}>
                  Siguiente número: {cai.establecimiento}-{cai.punto_emision}-01-{String(cai.siguiente).padStart(8, '0')}. Una factura emitida no se modifica: si tiene un error se anula.
                </p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
